/**
 * SB6-ANA-CACHE-01 — Viewer integration hook.
 *
 * Given the active image's full-res SAS URL, resolve it through the canonical-
 * key image cache (`imageCache.ts`):
 *
 * - **Cache hit** → an `URL.createObjectURL(blob)` for the cached bytes is
 *   returned. The Viewer's progressive-upgrade loads from the local blob:
 *   URL → zero network for the image bytes (story AC1/AC2).
 * - **Cache miss** → the original SAS URL is returned unchanged, so the
 *   existing progressive behaviour is fully preserved (AC3), AND the cache is
 *   populated in the background so the *next* visit hits.
 *
 * Object-URL lifecycle (AC4): every object URL this hook creates is revoked —
 * but NOT synchronously the instant the active image changes. The Viewer
 * (OpenSeadragon) loads the URL asynchronously, and during an image-switch
 * render the consumer can still be decoding the URL we just navigated away
 * from. Revoking it then produced `blob: … ERR_FILE_NOT_FOUND` and a cascading
 * "Couldn't load image" in the Viewer (reproduced against Azurite 2026-06-08;
 * the same race as staging). So we revoke through a small bounded ring: the
 * current URL and the one immediately before it always stay valid, older ones
 * are revoked as they age out, and everything is revoked on unmount — no leak,
 * no use-after-revoke.
 *
 * Scope is **full resolution only** — thumb120/thumb900 stay on the browser
 * HTTP cache. This hook is therefore wired into `progressiveImageUrl` only.
 */
import { useEffect, useRef, useState } from "react";
import { canonicalKey } from "./canonicalKey.js";
import { get as cacheGet, put as cachePut } from "./imageCache.js";
import { prefetchScheduler } from "./prefetchQueue.js";

/**
 * How many of the most-recently-created object URLs stay live before the oldest
 * is revoked. The race only needs the current image's URL plus the one being
 * navigated away from (an in-flight OSD rebuild may still be decoding it), so
 * 2 would suffice; 3 leaves a frame of margin. Bounds transient memory to a
 * handful of full-res blobs even while stepping quickly through a dataset.
 */
const KEEP_LIVE = 3;

/**
 * Resolve `fullUrl` to a cache-backed URL when possible.
 *
 * Returns the URL the Viewer should load as its progressive (full-res) source:
 * a `blob:` object URL on a cache hit, otherwise the original `fullUrl`
 * (`null` passes through to `null`). Crucially, it only ever returns a value
 * that belongs to the *current* `fullUrl` — while a fresh image's cache probe
 * runs (and during the render frame where `fullUrl` has just changed) it
 * returns `null` so the Viewer holds on thumb900 instead of being handed a
 * previous image's (possibly soon-to-be-revoked) blob: URL.
 */
export function useCachedImageUrl(fullUrl: string | null): string | null {
  // What the Viewer should load, tagged with the `fullUrl` it was resolved
  // FOR. The tag lets us detect — during render — that `fullUrl` has changed
  // but the effect hasn't re-resolved yet, so we never surface a stale blob.
  const [resolved, setResolved] = useState<{
    src: string;
    url: string;
  } | null>(null);

  // Bounded ring of object URLs we own and must eventually revoke. Held in a
  // ref so creating one doesn't re-run the unmount cleanup.
  const liveUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    // Mutable through a holder object (not a bare `let`) so TypeScript doesn't
    // narrow it to `false` at the `await` checkpoints below — the async IIFE
    // genuinely re-reads it after the effect's cleanup may have flipped it.
    const run = { cancelled: false };

    if (!fullUrl) {
      setResolved(null);
      return () => {
        run.cancelled = true;
      };
    }

    // data:/blob: URLs (test fixtures, already-local sources) are self-
    // contained — never route them through the cache. Pass straight through.
    if (fullUrl.startsWith("data:") || fullUrl.startsWith("blob:")) {
      setResolved({ src: fullUrl, url: fullUrl });
      return () => {
        run.cancelled = true;
      };
    }

    // Hold the Viewer on thumb900 (resolved = null) until the cache probe
    // resolves — a sub-millisecond Cache Storage lookup. This avoids issuing a
    // full-res SAS request that a cache hit would make redundant (AC1/AC2).
    // Keep a same-`fullUrl` resolution in place (e.g. an unrelated parent
    // re-render) so we don't flash null → rebuild OSD for no reason.
    setResolved((prev) => (prev?.src === fullUrl ? prev : null));

    const key = canonicalKey(fullUrl);

    void (async () => {
      try {
        const cached = await cacheGet(key);
        if (run.cancelled) return;

        if (cached) {
          // Hit — hand the Viewer a local blob: URL (zero network for bytes).
          const objectUrl = URL.createObjectURL(cached);
          const live = liveUrlsRef.current;
          live.push(objectUrl);
          // Revoke any URL that has aged out of the keep-window. The current
          // and immediately-previous URLs are retained so an in-flight OSD
          // load is never starved.
          while (live.length > KEEP_LIVE) {
            const old = live.shift();
            if (old) URL.revokeObjectURL(old);
          }
          setResolved({ src: fullUrl, url: objectUrl });
          return;
        }

        // Miss — expose the raw SAS URL so the Viewer runs its normal
        // progressive thumb900→full upgrade (AC3), then populate the cache in
        // the background so the next visit hits. The Viewer's <img> preload
        // doesn't expose its bytes, so we fetch our own copy to cache.
        setResolved({ src: fullUrl, url: fullUrl });
        // SB6-ANA-CACHE-02 dedupe — the prefetch queue owns the active image as
        // P0 and fires on the same activation. If it's already fetching (or has
        // cached) this key, skip our redundant populate fetch; the queue's
        // download lands in the same canonical-key bucket, so the next probe
        // hits. Only this image can race (P0); bounded to one avoided fetch.
        if (prefetchScheduler.isHandling(fullUrl)) return;
        const response = await fetch(fullUrl, { mode: "cors" });
        // Populate even if the effect has since been cancelled — caching bytes
        // we won't display this time is harmless and warms the next visit.
        if (!response.ok) return;
        await cachePut(key, response.clone());
      } catch {
        // Cache probe / network error — fall back to the raw SAS URL so the
        // Viewer can still load full-res; swallow so we never crash it.
        if (!run.cancelled) setResolved({ src: fullUrl, url: fullUrl });
      }
    })();

    return () => {
      run.cancelled = true;
    };
  }, [fullUrl]);

  // Revoke every object URL we still own when the hook unmounts — the leak
  // guard (AC4). Separate from the resolve effect so it runs once, on unmount,
  // and never mid-navigation.
  useEffect(() => {
    return () => {
      for (const u of liveUrlsRef.current) {
        URL.revokeObjectURL(u);
      }
      liveUrlsRef.current = [];
    };
  }, []);

  // Staleness guard — only surface the resolved URL when it belongs to the
  // CURRENT fullUrl. On the render frame where `fullUrl` has just changed but
  // the effect hasn't re-resolved, `resolved.src` still names the previous
  // image, so we return null (Viewer stays on thumb900) rather than handing it
  // a stale blob: URL that the ring may be about to revoke. Miss-path and
  // passthrough values have `src === url === fullUrl`, so they pass through.
  return resolved?.src === fullUrl ? resolved.url : null;
}
